import { Link, useFetcher } from "react-router";
import { useEffect, useState } from "react";
import RatingDisplay from "./shared/RatingDisplay";
import { ROUTES } from "~/services/const";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { RatingSelector } from "~/routes/play.$slug_id/RatingSelector";
import { Button } from "./ui/button";

type PlayRatingParams = {
  playId: string;
  userRating: number | null;
  averageRating: number;
  totalRatings: number;
  isAuthenticated: boolean;
};

export default function PlayRating({
  playId,
  userRating,
  averageRating,
  totalRatings,
  isAuthenticated,
}: PlayRatingParams) {
  const ratingLabel = totalRatings > 0 ? averageRating : null;
  const votesCountLabel = totalRatings === 1 ? "1 voto" : `${totalRatings} votos`;

  // const layoutAlignment = !!userRating ? "flex-row gap-4" : "flex-col gap-5 sm:flex-row lg:gap-8";
  const layoutAlignment = "flex-row gap-4";

  return (
    <div className={`flex ${layoutAlignment} items-center bg-white shadow-sm rounded gap-6 py-4 px-8`}>
      <RatingDisplay rating={ratingLabel} label={votesCountLabel} />
      <span className="block text-gray-200">|</span>
      <RatingAction playId={playId} isAuthenticated={isAuthenticated} userRating={userRating} />
    </div>
  );
}

type RatingActionParams = {
  playId: string;
  userRating: number | null;
  isAuthenticated: boolean;
};

function RatingAction({ playId, userRating, isAuthenticated }: RatingActionParams) {
  const [isMounted, setIsMounted] = useState(false);
  const fetcher = useFetcher();
  const [newUserRating, setNewUserRating] = useState(String(userRating) || "");

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return null;
  }

  if (fetcher.state !== "idle") {
    return <span className="font-medium text-slate-400 text-sm">Aguarde...</span>;
  }

  function triggerRemoveRating() {
    setNewUserRating("");

    const formData = new FormData();
    formData.append("playId", playId);
    formData.append("remove", "true");

    fetcher.submit(formData, { action: "/api/rate", method: "POST" });
  }

  if (!isAuthenticated) {
    return (
      <Link to={ROUTES.LOGIN} className="text-slate-600  font-medium text-sm lg:max-w-36">
        Inicia a sessão
        <br />
        para classificar a peça
      </Link>
    );
  }

  return (
    <Sheet>
      <SheetTrigger disabled={fetcher.state !== "idle"}>
        {userRating ? (
          <RatingDisplay rating={userRating} label="O teu voto" highlight={true} />
        ) : (
          <span className="text-slate-800 font-medium text-sm lg:max-w-36">🗳️ Avaliar</span>
        )}
      </SheetTrigger>
      <SheetContent className="flex flex-col items-center text-center" side="bottom">
        <SheetHeader>
          <SheetTitle>Como avalias esta peça?</SheetTitle>
          <SheetDescription>
            A tua avaliação entra para a média da peça. Podes alterar ou remover a qualquer momento.
          </SheetDescription>
        </SheetHeader>

        <RatingSelector initialRating={newUserRating} onUpdate={(value) => setNewUserRating(value)} />
        <fetcher.Form method="POST" action="/api/rate">
          <input type="hidden" value={playId} name="playId" />
          <input type="hidden" value={newUserRating} name="rating" />
          <div className="flex flex-col mt-4 gap-2 md:flex-row-reverse md:mb-4">
            <Button type="submit" disabled={fetcher.state !== "idle"}>
              Publicar Avaliação
            </Button>
            {!!userRating && (
              <Button variant="ghost" onClick={triggerRemoveRating} disabled={fetcher.state !== "idle"}>
                Remover avaliação
              </Button>
            )}
          </div>
        </fetcher.Form>
      </SheetContent>
    </Sheet>
  );
}
